/* eslint-disable max-lines */
/* eslint-disable max-len */
import { graphql } from "msw";
import { range } from "ramda";
import { 
    author,
    hasApproved,
    hasRejected,
    requiresApproval
} from "./mockinstancestates";
import {
    workflowConfigs
} from "./mockworkflowconfigs";

const workflowInstances = [
    requiresApproval, hasApproved, hasRejected, author
];

const users = [
    ...range(0, 10).map(i => ({ id: `${i}`, name: `User ${i}` })),
    // eslint-disable-next-line array-element-newline
    // eslint-disable-next-line no-magic-numbers
    ...range(11, 20).map(i => ({ id: `${i}`, name: `Group ${i - 10}` })),
];

const mockWorkflowStateBase = {
    allStages: [
        {
            assignee: [
                {
                    id: "2",
                    name: "User 2"
                }
            ],
            assigneeMode: "static",
            description: "Initial notes & points to be included in the document",
            id: "0",
            name: "Draft",
            nextStages: ["1"],
            onEnter: "sendEmail",
            onExit: "setPermissions"
        },
        {
            assignee: [
                {
                    id: "11",
                    name: "Group 1"
                }
            ],
            assigneeMode: "assignable",
            description: "First version with full content",
            id: "1",
            name: "Writing",
            nextStages: ["2"],
            onExit: "lockEntity"
        },
        {
            assignee: [
                {
                    id: "4",
                    name: "User 4"
                }
            ],
            assigneeMode: "assignable",
            description: "Final reviews and checks",
            id: "2",
            name: "Review",
            nextStages: ["3"],
            onEnter: "lockEntity"
        },
        {
            assignee: [
                {
                    id: "12",
                    name: "Group 2"
                }
            ],
            assigneeMode: "static",
            description: "Completed stage",
            id: "3",
            name: "Done",
            nextStages: [],
            onExit: "lockEntity"
        }
    ],
    assignableUsers: users,
    belongsTo: "1",
    currentProperties: {
        assignee: [
            {
                id: "user2",
                kind: "User",
                name: "User 2"
            }
        ],
        canProgress: true,
        currentStage: {
            assignee: [
                {
                    id: "1",
                    name: "User 1"
                }
            ],
            assigneeMode: "static",
            description: "Initial notes & points to be included in the document",
            id: "0",
            name: "Draft",
            nextStages: ["1"],
            onEnter: "sendEmail",
            onExit: "setPermissions"
        },
        description: "A test workflow",
        name: "Test"
    },
    pastChanges: [],
    template: "1234"
};

let workflowStates = [
    {
        ...mockWorkflowStateBase,
        type: "document",
        id: "global.document",
    },
    {
        ...mockWorkflowStateBase,
        type: "heading",
        id: "global.heading",
    },
    {
        ...mockWorkflowStateBase,
        type: "project",
        id: "global.project",
    },
];

export const handlers = [
    graphql.query("LoadAllWorkflowStates", (req, res, ctx) =>
        res(ctx.delay(), ctx.data(workflowStates))
    ),
    graphql.query("LoadWorkflowState", (req, res, ctx) => {
        const { id, type } = req.variables.payload;
        return res(
            ctx.delay(),
            ctx.data(
                workflowStates.find(
                    ({ id: entityId, type: entityType }) => id === entityId && type === entityType
                )
            )
        );
    }),
    graphql.mutation("UpdateWorkflowState", (req, res, ctx) => {
        const {
            workflowState: { id, type, ...rest },
        } = req.variables.payload;
        const updatedStates = workflowStates.map(state =>
            (state.id === id && state.type === type)
                ? { ...state, ...rest }
                : state
        );
        workflowStates = updatedStates;
        return res(ctx.delay(), ctx.data(workflowStates));
    }),
    graphql.mutation("UpdateWorkflowStateAssignees", (req, res, ctx) => {
        const { id, type, stage, assigned } = req.variables.payload;
        const targetState = workflowStates.find(
            ({ id: stateId, type:stateType }) => id === stateId && type === stateType
        );
        const targetStage = targetState.allStages.find(
            ({ id: stageId }) => stageId === stage
        );
        const updatedStage = { ...targetStage, assignee: assigned.map(({ value, label }) => ({ id: value, name: label })) };
        const updatedState = {
            ...targetState,
            allStages: targetState.allStages.map(wfStage =>
                wfStage.id === stage ? updatedStage : wfStage
            ),
        };
        const updatedStates = workflowStates.map(wf =>
            (wf.id === id && wf.type === type) ? updatedState : wf
        );
        workflowStates = updatedStates;
        return res(ctx.delay(), ctx.data(updatedState));
    }),
    graphql.mutation("ProgressWorkflowState", (req, res, ctx) => {
        const {
            payload: { targetStage, id: entityId, type: entityType },
        } = req.variables;
        const target = workflowStates.find(
            state => state.id === entityId && state.type === entityType
        );
        const nextStage = target.allStages.find(
            ({ id: sid }) => sid === targetStage
        );
        const progressedState = {
            ...target,
            currentProperties: {
                ...target.currentProperties,
                currentStage: nextStage,
                assignee: nextStage.assignee,
                canProgress:
                    target.allStages.findIndex(
                        ({ id: sid }) => sid === targetStage
                    ) <
                    target.allStages.length - 1,
            },
        };
        const updatedStates = workflowStates.map(state =>
            (state.id === entityId && state.type === entityType)
                ? progressedState
                : state
        );
        workflowStates = updatedStates;
        return res(ctx.delay(), ctx.data(progressedState));
    }),
    graphql.query("loadWidgetConfig", (req, res, ctx) => {
        return res(
            ctx.delay(),
            ctx.data({
                workflowwidgetsetting: {
                    currentConfiguration: workflowConfigs[req.variables.workflowid] || workflowConfigs[0]
                }
            })
        );
    }),
    graphql.query("currentWorkflowInstance", (req, res, ctx) => {
        res(
            ctx.delay(),
            ctx.data({
                v2document: {
                    id: "1",
                    workflowinstance: {
                        _id: "1"
                    }
                }
            })
        );
    }),
    graphql.query("getMyActions", (req, res, ctx) => {
        return res(
            ctx.delay(),
            ctx.data({
                myactions: {
                    edges: [
                        {
                            node: {
                                kind: "APPROVE",
                                status: "PENDING",
                                _parent: {
                                    _id: "3",
                                    kind: "v2document",
                                }
                            }
                        },
                        {
                            node: {
                                kind: "APPROVE",
                                status: "FULFILLED",
                                _parent: {
                                    _id: "2",
                                    kind: "v2document",
                                }
                            }
                        },
                    ]
                }
            })
        );
    }),
    // eslint-disable-next-line max-lines-per-function
    graphql.query("loadCurrentWorkflowInstance", (req, res, ctx) => {
        return res(
            ctx.delay(),
            ctx.data({
                workflowinstance: {
                    __typename: "Workflowinstance",
                    ...(workflowInstances[req.variables.id] || requiresApproval)
                }
            })
        );
    }),
    graphql.mutation("progressWorkflowInstance", (req, res, ctx) => {
        const instance = workflowInstances.find(({ etag }) => etag === req.variables.etag);
        const [{ _id: nextStageId }] = instance?.currentStage?.nextStages;
        if (!nextStageId) {
            return res(
                ctx.delay(),
                ctx.status("404"),
                ctx.data({ error: `could not find workflow instance with etag ${req.variables.etag}` })     
            );
        }

        instance.currentStage = instance.stages.find(({ _id }) => _id === nextStageId);

        return res(
            ctx.delay(),
            ctx.data({
                progressWorkflowInstance: {
                    __typename: "Workflowinstance",
                    ...instance
                }
            })
        );
    })
];
